import {
  JoinRoomFormValues,
  JoinRoomInitialValues,
  JoinRoomValidationSchema,
} from '@/pages/JoinRoom/validation'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux/store'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Form, Formik } from 'formik'
import { Label } from '../ui/label'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { joinRoom } from '@/redux/asyncThunks/studentRoom'
import ROUTES from '@/routes/routes'
import { UserRole } from '@/constants/room'

const JoinRoom: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { roomId } = useParams()
  const { isLoading } = useSelector((state: RootState) => state.room)
  const [errorMessage, setErrorMessage] = useState('')
  const user = useSelector((state: RootState) => state.user.user)

  const handleSubmit = async (values: JoinRoomFormValues) => {
    try {
      const resultAction = await dispatch(
        joinRoom({
          roomId: roomId!,
          inviteCode: values.inviteCode,
        })
      ).unwrap()
      if (resultAction.roomid && roomId) {
        if (user?.role === UserRole.Student) {
          navigate(ROUTES.STUDENT.WAITING_ROOM(roomId, values.inviteCode))
        } else {
          navigate(ROUTES.PROFESSOR.ROOM_DETAILS(roomId))
        }
      }
    } catch (error) {
      setErrorMessage(error as string)
    }
  }

  return (
    <div className='min-h-full pt-9'>
      <div className='w-1/2 mx-auto flex'>
        <div>
          <img
            src='/images/bird.svg'
            alt='Bird'
            className='h-[5.69rem] w-32 relative bottom-5'
          />
        </div>
        <div className='space-y-5'>
          <p className='text-2xl text-dark-blue font-normal text-center'>
            Welcome, {user?.name?.toLowerCase()}! Your adventure in the game
            starts now. Join a room to play.
          </p>
          <Card className='bg-[#FAFCFF] w-[425px] h-auto mx-auto py-14 px-4 rounded-lg border border-[#E6ECF2] backdrop-blur-xl'>
            <CardHeader className='p-0 pb-4'>
              <CardTitle className='text-base text-[#0F172A] font-bold text-center'>
                Join Room to Get Started
              </CardTitle>
            </CardHeader>
            <CardContent className='p-0'>
              <Formik
                initialValues={JoinRoomInitialValues}
                validationSchema={JoinRoomValidationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  isSubmitting,
                }) => (
                  <Form className='space-y-6'>
                    <div className='space-y-[0.38rem]'>
                      <Label
                        className={`text-sm font-normal ${
                          touched.inviteCode && errors.inviteCode
                            ? 'text-red-500'
                            : 'text-black'
                        }`}
                        htmlFor='inviteCode'
                      >
                        Enter Invite Code
                      </Label>
                      <Input
                        className={`h-9 placeholder:text-sm placeholder:text-[#99A2B0] placeholder:font-normal bg-[#FAFCFF] ${
                          touched.inviteCode && errors.inviteCode
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                        id='inviteCode'
                        name='inviteCode'
                        type='text'
                        placeholder='Enter Invite Code here'
                        value={values.inviteCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.inviteCode && errors.inviteCode && (
                        <div className='text-red-500 text-sm flex gap-2'>
                          <img src='/icons/alert-circle.svg' alt='Warning' />
                          {errors.inviteCode}
                        </div>
                      )}
                    </div>
                    {errorMessage && (
                      <p className='text-red-500 text-sm'>{errorMessage}</p>
                    )}
                    <Button
                      type='submit'
                      disabled={isSubmitting}
                      className='w-full text-xs font-normal bg-dark-blue hover:bg-dark-blue-hovered'
                    >
                      {isLoading ? 'Joining...' : 'Join Room'}
                    </Button>
                  </Form>
                )}
              </Formik>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default JoinRoom
